import React, { useState } from 'react';
import {
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  ArrayField,
  Datagrid,
  DateField,
  useNotify,
  useRefresh,
  useRecordContext,
  ShowProps,
  TopToolbar,
  ListButton,
} from 'react-admin';
import {
  Button,
  Grid,
  Paper,
  Stack,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import useAuthorize from '../hooks/useAuthorize';
import { LEADS_MODULE_ROLES } from '../roles/portalRoles';
import {
  addLeadActivity,
  queueSingleLeadEnrichment,
  updateLeadEnrichment,
} from './leadsApi';
import LeadCompanyDashboard from './LeadCompanyDashboard';

const ShowActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
);

const ReEnrichButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const handleClick = async () => {
    try {
      const result = await queueSingleLeadEnrichment(Number(record.id), true);
      notify(`Queued ${result.queued} enrichment jobs`, { type: 'info' });
      refresh();
    } catch (error: any) {
      notify(error?.body?.error || error?.message || 'Failed to queue enrichment', { type: 'error' });
    }
  };

  return (
    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleClick} title="Manual re-enrich (bypasses phase gate)">
      Re-enrich
    </Button>
  );
};

const ManualEnrichmentPanel = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [website, setWebsite] = useState('');
  const [turnover, setTurnover] = useState('');
  const [indiamartUrl, setIndiamartUrl] = useState('');
  const [note, setNote] = useState('');

  if (!record) return null;

  const handleSave = async () => {
    try {
      await updateLeadEnrichment(Number(record.id), {
        primary_website: website || record.primary_website,
        indiamart_profile_url: indiamartUrl || record.indiamart_profile_url,
        turnover_inr: turnover ? Number(turnover) : undefined,
        turnover_source: turnover ? 'manual' : undefined,
        turnover_confidence: turnover ? 0.95 : undefined,
      });
      if (note.trim()) {
        await addLeadActivity(Number(record.id), { activity_type: 'note', note: note.trim() });
      }
      notify('Enrichment updated', { type: 'info' });
      refresh();
    } catch (error: any) {
      notify(error?.body?.error || error?.message || 'Failed to save enrichment', { type: 'error' });
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Manual enrichment override
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MuiTextField
            fullWidth
            label="Website"
            defaultValue={record.primary_website || record.website || ''}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MuiTextField
            fullWidth
            label="IndiaMART profile URL"
            defaultValue={record.indiamart_profile_url || ''}
            onChange={(event) => setIndiamartUrl(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MuiTextField
            fullWidth
            label="Turnover (INR)"
            defaultValue={record.turnover_inr || ''}
            onChange={(event) => setTurnover(event.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <MuiTextField
            fullWidth
            multiline
            minRows={2}
            label="Activity note (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSave}>
            Save manual enrichment
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

const IntelligenceTab = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <ReEnrichButton />
      </Stack>
      <LeadCompanyDashboard record={record} />
    </>
  );
};

export const LeadCompanyShow: React.FC<ShowProps> = (props) => {
  useAuthorize(LEADS_MODULE_ROLES);

  return (
    <Show {...props} actions={<ShowActions />} title="Lead Company">
      <TabbedShowLayout>
        <Tab label="Intelligence">
          <IntelligenceTab />
        </Tab>

        <Tab label="Registry">
          <TextField source="master_company_id" label="Master ID" />
          <TextField source="company_name" />
          <TextField source="pan" />
          <TextField source="primary_gstin" />
          <TextField source="states" />
          <TextField source="primary_address" />
          <TextField source="bucket" />
          <TextField source="buyer_seller_category" label="Category" />
          <TextField source="platform_trade_names" />
          <TextField source="observed_sellers" />
          <TextField source="confirmed_seller_labels" />
          <TextField source="bucket_rationale" />
          <TextField source="acquisition_segment" />
          <TextField source="credco_relationship" />
        </Tab>

        <Tab label="Manual edit">
          <ManualEnrichmentPanel />
        </Tab>

        <Tab label="Contacts">
          <ArrayField source="contacts">
            <Datagrid bulkActionButtons={false}>
              <TextField source="contact_name" />
              <TextField source="phone" />
              <TextField source="email" />
              <TextField source="role_title" />
              <TextField source="is_primary" />
              <TextField source="source_label" />
            </Datagrid>
          </ArrayField>
        </Tab>

        <Tab label="Activity">
          <ArrayField source="activities">
            <Datagrid bulkActionButtons={false}>
              <TextField source="activity_type" />
              <TextField source="note" />
              <TextField source="assigned_to" />
              <DateField source="dt_created" showTime />
            </Datagrid>
          </ArrayField>
        </Tab>

        <Tab label="Jobs">
          <ArrayField source="recent_jobs">
            <Datagrid bulkActionButtons={false}>
              <TextField source="source" />
              <TextField source="status" />
              <TextField source="attempts" />
              <TextField source="result_summary" />
              <TextField source="last_error" />
              <DateField source="dt_updated" showTime />
            </Datagrid>
          </ArrayField>
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

export default LeadCompanyShow;
